import type { PageData } from "./data";
import { pageCanonicalPath } from "./policy";

export const DEFAULT_OG_TITLE = "Muhaha · Games & Dramas";
export const DEFAULT_OG_DESC = "Play community mini-games and watch interactive dramas. Play instantly, remix what you love.";

export interface OgMeta {
	title: string;
	description: string;
	image: string;
	url: string;
	type: string;
	locale?: string;
	keywords?: string[];
	noindex?: boolean;
	jsonLd?: unknown[];
}

export function pageMetadata(origin: string, path: string, page: PageData | null): OgMeta {
	const meta: OgMeta = {
		title: DEFAULT_OG_TITLE, description: DEFAULT_OG_DESC, image: `${origin}/og-default.png`,
		url: `${origin}${page ? pageCanonicalPath(page.path) : "/"}`, type: "website",
		noindex: page?.status === 404 || /^\/open\/(?:room|invite)\//.test(path),
		locale: page?.locale === "zh" ? "zh_CN" : "en_US",
	};
	if (page?.path.replace(/\/+$/, "") === "/all/tags") {
		meta.title = page.locale === "zh" ? "全部标签 · Muhaha" : "All tags · Muhaha";
	} else if (page?.playable) {
		const p = page.playable;
		meta.title = `${p.title} · Muhaha`;
		meta.description = p.summary || DEFAULT_OG_DESC;
		meta.image = p.coverUrl || meta.image;
		meta.noindex ||= !!p.visibility && p.visibility !== "PUBLIC";
		meta.noindex ||= !!p.status && p.status !== "PUBLISHED";
		if (p.kind === "drama") meta.type = "video.other";
		if (p.kind === "game") meta.jsonLd = [{ "@context": "https://schema.org", "@type": "VideoGame", name: p.title, url: meta.url, description: p.summary, image: p.coverUrl,
			...(!p.anonAuthor && p.author ? { author: { "@type": "Person", name: p.author.displayName || p.author.handle } } : {}) }];
		if (p.kind === "drama" && p.coverUrl && p.publishedAt) meta.jsonLd = [{ "@context": "https://schema.org", "@type": "VideoObject", name: p.title, thumbnailUrl: p.coverUrl, uploadDate: p.publishedAt, embedUrl: meta.url }];
	} else if (page?.profile) {
		const p = page.profile;
		const name = p.displayName || p.handle;
		meta.title = `${name} (@${p.handle}) · Muhaha`;
		meta.description = p.bio || DEFAULT_OG_DESC;
		meta.image = p.avatarUrl || meta.image;
		meta.type = "profile";
		meta.jsonLd = [{ "@context": "https://schema.org", "@type": "ProfilePage", mainEntity: { "@type": "Person", name, identifier: p.handle, url: meta.url } }];
	} else if (page?.doc) {
		meta.title = `${page.doc.title} · Muhaha`;
		meta.description = page.doc.description || DEFAULT_OG_DESC;
		meta.image = page.doc.ogImage || meta.image;
		meta.keywords = page.doc.keywords;
	} else if (page?.discover?.topic) meta.title = `#${page.discover.topic} · Muhaha`;
	else if (page?.path === "/") meta.jsonLd = [
		{ "@context": "https://schema.org", "@type": "Organization", name: "Muhaha", url: meta.url, logo: `${origin}/icon-512.png` },
		{ "@context": "https://schema.org", "@type": "WebSite", name: "Muhaha", url: meta.url },
	];
	return meta;
}
